import { EVENTS } from './analytics-events';

// Definizione globale per Google Tag Manager
declare global {
  interface Window {
    dataLayer: any[];
    gtag?: (...args: any[]) => void;
  }
}

type TrackValue = string | number | boolean | null | undefined;

const GA4_DISABLE_KEY = 'ga-disable-G-NV4QVRP40H';

/**
 * La sessione admin inibisce ogni invio: niente eventi nel dataLayer e niente GTM.
 * `window._adminAnalyticsOff` è letto anche dal loader in client/index.html.
 */
const isAdminTrackingOff = () =>
  typeof window !== 'undefined' && (window as any)._adminAnalyticsOff === true;

/**
 * GA4 va bloccato per due motivi indipendenti: rifiuto dei cookie analytics
 * (`_analyticsOptedOut`, dal banner) e sessione admin (`_adminAnalyticsOff`, da App).
 * `ga-disable-<ID>` li copre entrambi, quindi va ricalcolato a ogni cambio di uno dei due.
 */
export const syncAnalyticsOptOut = () => {
  if (typeof window === 'undefined') return;
  const w = window as any;
  if (w._analyticsOptedOut === true || w._adminAnalyticsOff === true) {
    w[GA4_DISABLE_KEY] = true;
  } else {
    delete w[GA4_DISABLE_KEY];
  }
};

/** Attiva o disattiva l'inibizione legata alla sessione admin. */
export const setAdminAnalyticsOff = (off: boolean) => {
  if (typeof window === 'undefined') return;
  (window as any)._adminAnalyticsOff = off;
  syncAnalyticsOptOut();
};

/**
 * Unico punto di push degli eventi al dataLayer GTM.
 * SSR-safe: no-op fuori dal browser. Rimuove i parametri null/undefined.
 */
export const track = (name: string, params: Record<string, TrackValue> = {}) => {
  if (typeof window === 'undefined' || isAdminTrackingOff()) return;
  window.dataLayer = window.dataLayer || [];
  const clean: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) clean[key] = value;
  }
  window.dataLayer.push({ event: name, ...clean, timestamp: new Date().toISOString() });
};

/**
 * Inizializza Google Tag Manager con controlli avanzati
 * GTM è già configurato tramite script in index.html
 */
export const initGTM = () => {
  if (typeof window === 'undefined' || isAdminTrackingOff()) return;
  
  // Inizializza dataLayer se non esiste
  window.dataLayer = window.dataLayer || [];
  
  // Funzione gtag per compatibilità
  window.gtag = window.gtag || function() {
    window.dataLayer.push(arguments);
  };
  
  // Verifica che GTM sia caricato
  const checkGTMLoaded = () => {
    return window.dataLayer && window.dataLayer.length > 0;
  };
  
  // Attendi che GTM sia caricato prima di procedere
  const waitForGTM = (callback: () => void, maxAttempts = 50) => {
    let attempts = 0;
    const checkInterval = setInterval(() => {
      attempts++;
      if (checkGTMLoaded() || attempts >= maxAttempts) {
        clearInterval(checkInterval);
        callback();
      }
    }, 100);
  };
  
  waitForGTM(() => {
    // Configura debug e identificatori solo se non già inizializzato
    const isAlreadyInitialized = window.dataLayer.some(item => item.event === 'gtm_init_custom');
    
    if (!isAlreadyInitialized) {
      window.dataLayer.push({
        'event': 'gtm_init_custom',
        'gtm.uniqueEventId': Math.random().toString(36).substr(2, 9),
        'page_location': window.location.href,
        'page_title': document.title,
        'timestamp': new Date().toISOString()
      });
      
      if (import.meta.env.DEV) {
        console.log('Google Tag Manager inizializzato');
        console.log('DataLayer disponibile:', !!window.dataLayer);
        console.log('GTM Container ID: GTM-TH7VN33L');
        console.log('Current URL:', window.location.href);
      }
    }
  });
};

/**
 * Traccia visualizzazioni di pagina con Google Tag Manager
 * @param url L'URL della pagina da tracciare
 * @param title Titolo della pagina (opzionale)
 * @param additionalData Dati aggiuntivi da includere nell'evento (opzionale)
 */
export const trackPageView = (url: string, title?: string, additionalData?: Record<string, any>) => {
  if (typeof window === 'undefined' || isAdminTrackingOff()) return;
  
  // Assicurati che dataLayer esista (dovrebbe sempre esistere)
  window.dataLayer = window.dataLayer || [];
  
  // Attendi che dataLayer sia inizializzato
  setTimeout(() => {
    // Verifica che non sia una pagina duplicata consecutiva DOPO il timeout
    // per evitare race conditions
    const lastPageView = window.dataLayer
      .slice()
      .reverse()
      .find(item => item.event === 'page_view' && item.page_path === url);
    
    // Verifica anche l'ora per evitare bloccare navigazioni legittime
    const lastPageViewTime = lastPageView?.timestamp 
      ? new Date(lastPageView.timestamp).getTime() 
      : 0;
    const now = Date.now();
    const timeSinceLastPageView = now - lastPageViewTime;
    
    // Blocca solo se è la stessa pagina E è passato meno di 1 secondo
    if (lastPageView && timeSinceLastPageView < 1000) {
      if (import.meta.env.DEV) {
        console.log('GTM: Page view blocked (duplicate within 1s):', url);
      }
      return;
    }
    
    const pageData: any = {
      event: 'page_view',
      page_path: url,
      page_location: window.location.href,
      page_title: title || document.title,
      timestamp: new Date().toISOString(),
      ...additionalData
    };
    
    window.dataLayer.push(pageData);
    if (import.meta.env.DEV) {
      console.log('GTM: Page view tracked:', pageData);
    }
  }, 100);

  // Aggiungi un fallback per verificare che GTM si carichi
  if (import.meta.env.DEV) {
    setTimeout(() => {
      const gtmLoaded = !!document.querySelector('script[src*="googletagmanager.com/gtm.js"]');
      if (!gtmLoaded) {
        console.warn('GTM: Script non ancora caricato, ma evento salvato nel dataLayer');
      }
    }, 2000);
  }
};

/**
 * Traccia visualizzazioni di pagine di schede storiche (entità) con informazioni sul tipo di entità
 * @param url L'URL della pagina da tracciare
 * @param title Titolo della pagina (opzionale)
 * @param entityTypeCode Il codice del tipo di entità (es. 'building', 'street', 'square')
 * @param entityTypeName Il nome del tipo di entità (es. 'Edificio', 'Via', 'Piazza')
 * @param entityId L'ID dell'entità (opzionale)
 * @param entityName Il nome dell'entità (opzionale)
 */
export const trackEntityPageView = (
  url: string,
  title?: string,
  entityTypeCode?: string,
  entityTypeName?: string,
  entityId?: number,
  entityName?: string
) => {
  if (typeof window === 'undefined') return;
  
  const additionalData: Record<string, any> = {
    content_type: 'entity',
    page_type: 'entity_detail'
  };
  
  if (entityTypeCode) {
    additionalData.entity_type_code = entityTypeCode;
  }
  
  if (entityTypeName) {
    additionalData.entity_type_name = entityTypeName;
  }
  
  if (entityId !== undefined) {
    additionalData.entity_id = entityId;
  }
  
  if (entityName) {
    additionalData.entity_name = entityName;
  }
  
  // Chiama trackPageView con i dati aggiuntivi
  trackPageView(url, title, additionalData);
};

/**
 * Traccia visualizzazioni di pagine foto storiche con page_type dedicato
 * @param url L'URL della pagina da tracciare
 * @param title Titolo della pagina (opzionale)
 * @param photoId L'ID della foto (opzionale)
 * @param photoTitle Il titolo della foto (opzionale)
 */
export const trackPhotoPageView = (
  url: string,
  title?: string,
  photoId?: number,
  photoTitle?: string
) => {
  if (typeof window === 'undefined') return;

  const additionalData: Record<string, any> = {
    content_type: 'photo',
    page_type: 'photo_detail'
  };

  if (photoId !== undefined) {
    additionalData.photo_id = photoId;
  }

  if (photoTitle) {
    additionalData.photo_title = photoTitle;
  }

  // Chiama trackPageView con i dati aggiuntivi
  trackPageView(url, title, additionalData);
};

/**
 * Traccia eventi personalizzati con Google Tag Manager
 * @param action L'azione dell'evento
 * @param category La categoria dell'evento (opzionale)
 * @param label L'etichetta dell'evento (opzionale)
 * @param value Valore numerico dell'evento (opzionale)
 */
export const trackEvent = (
  action: string,
  category?: string,
  label?: string,
  // NOTE: value resta nella firma per retro-compatibilità ma non è più inviato (nessun caller lo usa).
  value?: number
) => {
  track(EVENTS.CUSTOM_EVENT, {
    event_action: action,
    event_category: category || 'general',
    event_label: label,
  });
};

/**
 * Verifica lo stato di GTM e fornisce informazioni di debug
 */
const debugGTM = () => {
  if (typeof window === 'undefined') {
    console.log('GTM Debug: Window non disponibile (SSR)');
    return;
  }
  
  console.log('=== GTM DEBUG INFO ===');
  console.log('DataLayer exists:', !!window.dataLayer);
  console.log('DataLayer length:', window.dataLayer?.length || 0);
  console.log('DataLayer content:', window.dataLayer);
  console.log('Current URL:', window.location.href);
  console.log('GTM Scripts loaded:', !!document.querySelector('script[src*="googletagmanager.com"]'));
  console.log('GA Script loaded:', !!document.querySelector('script[src*="google-analytics.com"]'));
  console.log('======================');
  
  return {
    hasDataLayer: !!window.dataLayer,
    dataLayerLength: window.dataLayer?.length || 0,
    hasGTMScript: !!document.querySelector('script[src*="googletagmanager.com"]'),
    hasGAScript: !!document.querySelector('script[src*="google-analytics.com"]'),
    currentUrl: window.location.href
  };
};

/**
 * Testa il funzionamento di GTM inviando un evento di test
 */
const testGTM = () => {
  if (typeof window === 'undefined' || !window.dataLayer) {
    console.warn('GTM: Non disponibile per il test');
    return false;
  }
  
  const testEvent = {
    event: 'gtm_test_event',
    test_action: 'gtm_functionality_test',
    test_timestamp: new Date().toISOString(),
    test_id: Math.random().toString(36).substr(2, 9)
  };
  
  window.dataLayer.push(testEvent);
  console.log('GTM: Test event inviato:', testEvent);
  return true;
};

/**
 * Traccia le condivisioni social di foto, entità o approfondimenti con Google Analytics 4 e Google Tag Manager
 * @param platform La piattaforma social (fb, x, pinterest, whatsapp)
 * @param contentType Il tipo di contenuto ('photo', 'entity' o 'essay')
 * @param itemId L'ID del contenuto
 * @param itemTitle Il titolo del contenuto
 * @param itemUrl L'URL del contenuto da condividere
 */
export type ShareContentType = 'photo' | 'entity' | 'essay' | 'itinerary' | 'neighborhood';

export const trackSocialShare = (
  platform: 'fb' | 'x' | 'pinterest' | 'whatsapp' | 'copy_link',
  contentType: ShareContentType,
  itemId: number,
  itemTitle: string,
  itemUrl: string
) => {
  const itemIdKey = `${contentType}_id`;
  const itemTitleKey = `${contentType}_title`;
  const itemUrlKey = `${contentType}_url`;
  track(EVENTS.SHARE, {
    method: platform,
    social_network: platform,
    social_action: 'share',
    content_type: contentType,
    item_id: itemId.toString(),
    item_name: itemTitle,
    item_url: itemUrl,
    content_url: itemUrl,
    [itemIdKey]: itemId,
    [itemTitleKey]: itemTitle,
    [itemUrlKey]: itemUrl,
  });
};

/**
 * Traccia l'aggiunta o rimozione di foto dai preferiti con Google Analytics 4 e Google Tag Manager
 * @param action L'azione eseguita ('add' o 'remove')
 * @param photoId L'ID della foto
 * @param photoTitle Il titolo della foto
 * @param photoUrl L'URL della foto
 * @param context Il contesto dove è avvenuta l'azione ('list' per listato, 'detail' per scheda foto)
 */
export const trackFavorite = (
  action: 'add' | 'remove',
  photoId: number,
  photoTitle: string,
  photoUrl: string,
  context: 'list' | 'detail'
) => {
  track(EVENTS.FAVORITE, {
    action,
    item_id: photoId.toString(),
    item_name: photoTitle,
    item_url: photoUrl,
    photo_id: photoId,
    photo_title: photoTitle,
    photo_url: photoUrl,
    favorite_context: context,
  });
};

/**
 * Traccia l'aggiunta o rimozione di entità dai segnalibri con Google Analytics 4 e Google Tag Manager
 * @param action L'azione eseguita ('add' o 'remove')
 * @param entityId L'ID dell'entità
 * @param entityTitle Il titolo/nome dell'entità
 * @param entityUrl L'URL dell'entità
 * @param context Il contesto dove è avvenuta l'azione ('list' per listato, 'detail' per scheda entità)
 */
export const trackBookmark = (
  action: 'add' | 'remove',
  entityId: number,
  entityTitle: string,
  entityUrl: string,
  context: 'list' | 'detail'
) => {
  track(EVENTS.BOOKMARK, {
    action,
    item_id: entityId.toString(),
    item_name: entityTitle,
    item_url: entityUrl,
    entity_id: entityId,
    entity_title: entityTitle,
    entity_url: entityUrl,
    bookmark_context: context,
  });
};

/**
 * Traccia le registrazioni utente con Google Analytics 4 e Google Tag Manager
 * @param method Il metodo di registrazione ('email', 'google', 'facebook')
 * @param userId L'ID dell'utente (opzionale, per privacy)
 * @param userEmail L'email dell'utente (opzionale, per privacy - può essere hash o null)
 * @param isNewUser Se è un nuovo utente o un login esistente (per registrazioni social)
 */
export const trackRegistration = (
  method: 'email' | 'google' | 'facebook',
  userId?: number,
  userEmail?: string | null,
  isNewUser: boolean = true
) => {
  track(isNewUser ? EVENTS.SIGN_UP : EVENTS.LOGIN, {
    method,
    registration_source: method === 'email' ? 'email' : 'social',
    social_provider: method === 'email' ? null : method,
    is_new_user: isNewUser,
    user_id: userId !== undefined ? userId.toString() : undefined,
  });
};

/**
 * Traccia i click sul widget "Seguici su Facebook" con Google Analytics 4 e Google Tag Manager
 * @param pageType Il tipo di pagina dove è stato cliccato il widget ('photo_detail' o 'entity_detail')
 * @param itemId L'ID della foto o entità (opzionale)
 * @param itemTitle Il titolo della foto o entità (opzionale)
 * @param itemUrl L'URL della foto o entità (opzionale)
 */
export const trackFacebookCTAClick = (
  pageType: 'photo_detail' | 'entity_detail' | 'neighborhood_detail',
  itemId?: number,
  itemTitle?: string,
  itemUrl?: string
) => {
  const contentType = pageType === 'photo_detail' ? 'photo' : pageType === 'entity_detail' ? 'entity' : 'neighborhood';
  const itemIdKey = `${contentType}_id`;
  const itemTitleKey = `${contentType}_title`;
  const itemUrlKey = `${contentType}_url`;
  track(EVENTS.FACEBOOK_CTA_CLICK, {
    page_type: pageType,
    content_type: contentType,
    [itemIdKey]: itemId,
    [itemTitleKey]: itemTitle,
    [itemUrlKey]: itemUrl,
  });
};

/**
 * Traccia i click sulla CTA di registrazione nello slot di sidebar delle pagine di dettaglio
 * @param context Il tipo di pagina che ospita il widget
 * @param provider Il provider social scelto per la registrazione
 * @param itemId L'ID della foto, entità o contenuto (opzionale)
 */
export const trackRegisterCtaClick = (
  context: 'photo_detail' | 'entity_detail' | 'essay_detail' | 'itinerary_detail' | 'neighborhood_detail',
  provider: 'google' | 'facebook',
  itemId?: number,
) => {
  track(EVENTS.SIGN_UP_START, {
    method: provider,
    source: `cta_widget_${context}`,
    content_id: itemId !== undefined ? String(itemId) : undefined,
  });
};

export type DonateSource = 'photo_detail' | 'essay' | 'itinerary' | 'footer' | 'support_page' | 'about' | 'neighborhood' | 'share_bar' | 'home_hero';

/**
 * Traccia i click verso il sostegno Ko-fi con Google Analytics 4 e Google Tag Manager
 * @param source La posizione da cui parte il click ('photo_detail', 'essay', 'itinerary', 'footer' o 'support_page' per il link-out effettivo verso Ko-fi)
 */
export const trackDonateClick = (source: DonateSource) => {
  track(EVENTS.DONATE_CLICK, {
    source,
  });
};

export type QuizEventName =
  | 'quiz_start'
  | 'quiz_entry_click'
  | 'quiz_level_completed'
  | 'quiz_wall_shown'
  | 'quiz_signup_click'
  | 'quiz_signup_completed'
  | 'badge_share_click'
  | 'badge_landing_view'
  | 'quiz_share_open'
  | 'quiz_share_channel'
  | 'quiz_shared_result_view'
  | 'quiz_shared_result_play';

/**
 * Eventi dell'imbuto quiz. Non si traccia la singola risposta:
 * quel dato vive già su timesServed / timesCorrect nel database.
 */
export const trackQuiz = (name: QuizEventName, params: Record<string, TrackValue> = {}) => {
  track(name, params);
};
